import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Grid,
  Group,
  NativeSelect,
  ScrollArea,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { IconCheck, IconRefresh, IconSparkles } from '@/components/icons'
import { EmptyState, PageCenterLoader, PageShell } from '@/components/ui'
import { useAuth } from '@/lib/auth-context'
import { api, ApiError } from '@/lib/api'
import { parseApiDateUtc } from '@/lib/api-date'
import { formatDateTimeDot } from '@/lib/ticket-display'
import {
  ACT_FIELD_LABELS,
  ACT_TYPE_LABELS,
  type ActItem,
  type ActMasterItem,
  type ActNormalized,
  type ActQualityReport,
  buildActMasterItems,
  buildDiffPackageForApply,
  effectiveFieldsForRow,
  getEditableValue,
  isLongActField,
  normalizeActualizationUi,
  updateActItemField,
} from '@/lib/actualization-utils'
import {
  mergeWithDefaultPermissions,
  hasPermission,
  loadMyPermissions,
  readCachedPermissions,
} from '@/lib/permissions'
import type { MyPermissionsResponse, ProjectRow } from '@/types/api'

type ActualizationMode = 'diff' | 'product_document' | 'temporary_instruction'
type ProductApplyMode = 'add' | 'replace_all' | 'no_materials'

type ProductAiVariable = {
  key?: string
  description?: string | null
  required?: boolean
  ask_label?: string | null
  priority?: number | null
}

type ProductAiPatch = {
  instructions_system_prompt?: string
  variables?: ProductAiVariable[]
}

type TemporaryInstructionPack = {
  instruction?: Record<string, unknown>
  kb_items?: Record<string, unknown>[]
  warnings?: string[]
}

function ActReadonlyField({ fieldKey, value }: { fieldKey: string; value: unknown }) {
  const label = ACT_FIELD_LABELS[fieldKey] || fieldKey
  const boolVal = typeof value === 'boolean' || fieldKey === 'is_active'
  const v = boolVal ? (value ? 'Да' : 'Нет') : String(value ?? '')
  if (isLongActField(fieldKey)) {
    return <Textarea label={label} readOnly autosize minRows={3} value={v} />
  }
  return <TextInput label={label} readOnly value={v} />
}

function ActEditField({
  fieldKey,
  value,
  onChange,
}: {
  fieldKey: string
  value: unknown
  onChange: (v: unknown) => void
}) {
  const label = ACT_FIELD_LABELS[fieldKey] || fieldKey
  if (typeof value === 'boolean' || fieldKey === 'is_active') {
    return (
      <Checkbox
        label={label}
        checked={!!value}
        onChange={(e) => onChange(e.currentTarget.checked)}
      />
    )
  }
  if (isLongActField(fieldKey)) {
    return (
      <Textarea
        label={label}
        value={String(value ?? '')}
        onChange={(e) => onChange(e.target.value)}
        minRows={3}
        autosize
        maxRows={14}
      />
    )
  }
  return (
    <TextInput
      label={label}
      value={String(value ?? '')}
      onChange={(e) => onChange(e.target.value)}
    />
  )
}

function QualityReportSection({ report }: { report: ActQualityReport }) {
  const items = report.items || []
  const th = Number(report.confidence_threshold ?? 0).toFixed(2)
  return (
    <Card withBorder radius="md" padding="sm">
      <Text fw={600} size="sm" mb="xs">
        Отчёт качества (dry-run)
      </Text>
      <Text size="xs" c="dimmed" mb="sm">
        Порог {th} · всего {report.total_items ?? 0} · оставлено {report.kept_items ?? 0} · отфильтровано{' '}
        {report.skipped_items ?? 0}
      </Text>
      <Stack gap="xs">
        {items.map((x, i) => (
          <Card key={i} withBorder padding="xs" radius="md" bg="var(--mantine-color-body)">
            <Group justify="space-between" wrap="nowrap" gap="xs">
              <Text size="sm" fw={500}>
                {String(x.decision || '')}
              </Text>
              <Badge size="xs" variant="light" color={x.status === 'kept' ? 'green' : 'yellow'}>
                {String(x.status || '')}
              </Badge>
            </Group>
            <Text size="xs" c="dimmed">
              confidence: {x.confidence == null ? 'n/a' : Number(x.confidence).toFixed(2)}
              {x.existing_id ? ` · id: ${x.existing_id}` : ''}
            </Text>
            <Text size="sm" mt={4}>
              {String(x.preview || '')}
            </Text>
            <Text size="xs" c="dimmed">
              {String(x.reason || 'Без пояснения')}
            </Text>
          </Card>
        ))}
      </Stack>
    </Card>
  )
}

export function ActualizationPage() {
  const { user } = useAuth()
  const [perm, setPerm] = useState<MyPermissionsResponse | null>(() => readCachedPermissions())
  useEffect(() => {
    void loadMyPermissions(user?.role).then(setPerm)
  }, [user?.role])
  const effectivePerm = mergeWithDefaultPermissions(perm, user?.role)
  const canAct = hasPermission(effectivePerm, 'access_actualization')

  const [projects, setProjects] = useState<ProjectRow[]>([])
  const [projectId, setProjectId] = useState('')
  const [userInput, setUserInput] = useState('')
  const [analyzing, setAnalyzing] = useState(false)
  const [applying, setApplying] = useState(false)
  const [pkg, setPkg] = useState<ActNormalized | null>(null)
  const [mode, setMode] = useState<ActualizationMode>('diff')
  const [productOpenaiKey, setProductOpenaiKey] = useState('')
  const [productModel, setProductModel] = useState('gpt-5.2')
  const [productModelOptions, setProductModelOptions] = useState<string[]>([])
  const [productModelsLoading, setProductModelsLoading] = useState(false)
  const [productAiPatch, setProductAiPatch] = useState<ProductAiPatch | null>(null)
  const [productApplyMode, setProductApplyMode] = useState<ProductApplyMode>('add')
  const [tempPack, setTempPack] = useState<TemporaryInstructionPack | null>(null)
  const [tempTitle, setTempTitle] = useState('')
  const [tempStartLocal, setTempStartLocal] = useState('')
  const [tempEndLocal, setTempEndLocal] = useState('')
  const [tempItems, setTempItems] = useState<Record<string, unknown>[]>([])
  const [editingTempId, setEditingTempId] = useState<string | null>(null)
  const [rawAnalyzeError, setRawAnalyzeError] = useState<string | null>(null)
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const [included, setIncluded] = useState<Set<string>>(() => new Set())
  const [applyNow, setApplyNow] = useState(false)
  const [applyAtLocal, setApplyAtLocal] = useState('')
  const [jobs, setJobs] = useState<
    {
      id: string
      status: string
      apply_at_utc: string | null
      error: string | null
      payload?: Record<string, unknown> | null
      created_at: string
    }[]
  >([])

  useEffect(() => {
    let alive = true
    void api.projects().then((p) => {
      if (alive) setProjects(Array.isArray(p) ? p : [])
    })
    return () => {
      alive = false
    }
  }, [])

  const masterItems = useMemo(() => (pkg ? buildActMasterItems(pkg) : []), [pkg])
  const isProductDocumentMode = mode === 'product_document'
  const isTemporaryMode = mode === 'temporary_instruction'

  useEffect(() => {
    if (masterItems.length && !selectedKey) {
      setSelectedKey(masterItems[0].key)
    }
  }, [masterItems, selectedKey])

  useEffect(() => {
    if (pkg && masterItems.length) {
      setIncluded(new Set(masterItems.map((m) => m.key)))
    }
  }, [pkg, masterItems])

  const loadJobs = useCallback(async () => {
    if (!projectId) {
      setJobs([])
      return
    }
    try {
      const res = await api.projectActualizationJobs(projectId)
      setJobs(res.items ?? [])
    } catch {
      setJobs([])
    }
  }, [projectId])

  const loadTemporaryItems = useCallback(async () => {
    if (!projectId) {
      setTempItems([])
      return
    }
    try {
      const res = await api.projectTemporaryInstructions(projectId)
      setTempItems(res.items || [])
    } catch {
      setTempItems([])
    }
  }, [projectId])

  useEffect(() => {
    void loadJobs()
    void loadTemporaryItems()
  }, [loadJobs, loadTemporaryItems])

  const onAnalyze = async () => {
    const pid = projectId.trim()
    const text = userInput.trim()
    if (!pid || text.length < (isProductDocumentMode ? 50 : isTemporaryMode ? 10 : 3)) {
      showNotification({
        title: 'Форма',
        message: isProductDocumentMode
          ? 'Выберите проект и вставьте продуктовый документ не короче 50 символов.'
          : isTemporaryMode
            ? 'Выберите проект и опишите временную инструкцию не короче 10 символов.'
          : 'Выберите проект и опишите изменения (не короче 3 символов).',
        color: 'yellow',
      })
      return
    }
    if ((isProductDocumentMode || isTemporaryMode) && productOpenaiKey.trim().length < 10) {
      showNotification({
        title: 'OpenAI',
        message: 'Укажите API-ключ OpenAI для генерации.',
        color: 'yellow',
      })
      return
    }
    setAnalyzing(true)
    setRawAnalyzeError(null)
    setPkg(null)
    setProductAiPatch(null)
    setTempPack(null)
    setSelectedKey(null)
    try {
      if (isTemporaryMode) {
        const data = await api.projectTemporaryInstructionGenerate(pid, {
          openai_api_key: productOpenaiKey.trim(),
          model: productModel.trim() || 'gpt-5.2',
          source_text: text,
        })
        setTempPack(data as TemporaryInstructionPack)
        const instruction = (data as TemporaryInstructionPack).instruction || {}
        setTempTitle(String(instruction.category || 'Временная инструкция'))
        return
      }
      const data = isProductDocumentMode
        ? await api.projectProductDocumentGenerate(pid, {
            openai_api_key: productOpenaiKey.trim(),
            model: productModel.trim() || 'gpt-5.2',
            product_document: text,
          })
        : await api.projectActualizationAnalyze(pid, { user_input: text })
      const normalized = normalizeActualizationUi(data as Record<string, unknown>)
      setPkg(normalized)
      const patch = (data as Record<string, unknown>).project_ai_patch
      setProductAiPatch(isProductDocumentMode && patch && typeof patch === 'object' ? (patch as ProductAiPatch) : null)
      void loadJobs()
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'Не удалось выполнить анализ'
      setRawAnalyzeError(msg)
      showNotification({ title: 'Ошибка', message: msg, color: 'red' })
    } finally {
      setAnalyzing(false)
    }
  }

  const onApply = async () => {
    const pid = projectId.trim()
    if (!pid || !pkg) return
    if (isProductDocumentMode && !applyNow) {
      showNotification({
        title: 'Продуктовый документ',
        message: 'Комплект из продуктового документа пока применяется только немедленно, чтобы не потерять системный промпт и переменные.',
        color: 'yellow',
      })
      return
    }
    if (!applyNow && !applyAtLocal.trim()) {
      showNotification({
        title: 'Время применения',
        message: 'Укажите дату/время или включите «Применить немедленно».',
        color: 'yellow',
      })
      return
    }
    const apply_at_utc = applyNow ? null : new Date(applyAtLocal).toISOString()
    const diff_package = buildDiffPackageForApply(pkg, included)
    setApplying(true)
    try {
      if (isProductDocumentMode) {
        await api.projectProductDocumentApply(pid, {
          apply_now: true,
          apply_at_utc: null,
          diff_package,
          project_ai_patch: productAiPatch as Record<string, unknown> | null,
          apply_mode: productApplyMode,
        })
      } else {
        await api.projectActualizationApply(pid, {
          apply_now: applyNow,
          apply_at_utc,
          diff_package,
        })
      }
      showNotification({
        title: applyNow ? 'Применено' : 'Запланировано',
        message: isProductDocumentMode
          ? 'Комплект проекта применён: инструкции/KB, системный промпт и переменные обновлены.'
          : applyNow ? 'Изменения записаны в проект.' : 'Задача поставлена в очередь.',
        color: 'green',
      })
      setPkg(null)
      setProductAiPatch(null)
      setProductApplyMode('add')
      void loadJobs()
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось применить',
        color: 'red',
      })
    } finally {
      setApplying(false)
    }
  }

  const onScheduleTemporaryInstruction = async () => {
    const pid = projectId.trim()
    if (!pid || !tempPack?.instruction) return
    if (!tempTitle.trim() || !tempStartLocal || !tempEndLocal) {
      showNotification({
        title: 'Временная инструкция',
        message: 'Укажите название, дату старта и дату окончания.',
        color: 'yellow',
      })
      return
    }
    const start = new Date(tempStartLocal)
    const end = new Date(tempEndLocal)
    if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime()) || end <= start) {
      showNotification({
        title: 'Временная инструкция',
        message: 'Дата окончания должна быть позже даты старта.',
        color: 'yellow',
      })
      return
    }
    setApplying(true)
    try {
      if (editingTempId) {
        await api.updateProjectTemporaryInstruction(pid, editingTempId, {
          title: tempTitle.trim(),
          start_at_utc: start.toISOString(),
          end_at_utc: end.toISOString(),
          instruction: tempPack.instruction,
        })
      } else {
        await api.projectTemporaryInstructionSchedule(pid, {
          title: tempTitle.trim(),
          start_at_utc: start.toISOString(),
          end_at_utc: end.toISOString(),
          instruction: tempPack.instruction,
          kb_items: tempPack.kb_items || [],
        })
      }
      showNotification({
        title: editingTempId ? 'Обновлено' : 'Запланировано',
        message: editingTempId
          ? 'Временная инструкция обновлена; расписание пересоздано, активность синхронизирована с окном дат.'
          : 'Инструкция включится и выключится автоматически, ассистент пересоберётся в обе даты.',
        color: 'green',
      })
      setTempPack(null)
      setEditingTempId(null)
      setUserInput('')
      void loadJobs()
      void loadTemporaryItems()
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось запланировать временную инструкцию',
        color: 'red',
      })
    } finally {
      setApplying(false)
    }
  }

  const toDatetimeLocalValue = (isoUtc: unknown): string => {
    const d = parseApiDateUtc(isoUtc == null ? null : String(isoUtc))
    if (!d) return ''
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
  }

  const editTemporaryItem = (it: Record<string, unknown>) => {
    const instruction = (it.instruction && typeof it.instruction === 'object' ? it.instruction : {}) as Record<string, unknown>
    setEditingTempId(String(it.id || ''))
    setTempTitle(String(it.title || instruction.category || 'Временная инструкция'))
    setTempPack({ instruction, kb_items: [], warnings: [] })
    setTempStartLocal(toDatetimeLocalValue(it.start_at_utc))
    setTempEndLocal(toDatetimeLocalValue(it.end_at_utc))
    setUserInput('')
    window.setTimeout(() => {
      document.getElementById('temporary-instruction-editor')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 50)
  }

  const onDeleteTemporaryItem = async (it: Record<string, unknown>) => {
    const pid = projectId.trim()
    const id = String(it.id || '')
    if (!pid || !id) return
    const status = String(it.status || '')
    const msg =
      status === 'scheduled'
        ? 'Отменить и удалить ещё не включённую временную инструкцию (jobs + KB)?'
        : status === 'active'
          ? 'Удалить активную временную инструкцию сейчас? Она сразу выключится.'
          : 'Удалить временную инструкцию и связанные материалы?'
    if (!window.confirm(msg)) return
    setApplying(true)
    try {
      await api.deleteProjectTemporaryInstruction(pid, id)
      if (editingTempId === id) {
        setEditingTempId(null)
        setTempPack(null)
      }
      showNotification({
        title: 'Удалено',
        message: 'Временная инструкция и связанные материалы удалены.',
        color: 'green',
      })
      void loadJobs()
      void loadTemporaryItems()
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось удалить временную инструкцию',
        color: 'red',
      })
    } finally {
      setApplying(false)
    }
  }

  const onStopTemporaryItem = async (it: Record<string, unknown>) => {
    const pid = projectId.trim()
    const id = String(it.id || '')
    if (!pid || !id) return
    const status = String(it.status || '')
    const msg =
      status === 'scheduled'
        ? 'Отменить запланированное включение? Инструкция не удалится, но больше не включится.'
        : 'Досрочно выключить инструкцию сейчас? Расписание оставшихся jobs будет отменено.'
    if (!window.confirm(msg)) return
    setApplying(true)
    try {
      await api.stopProjectTemporaryInstruction(pid, id)
      if (editingTempId === id) {
        setEditingTempId(null)
        setTempPack(null)
      }
      showNotification({
        title: 'Остановлено',
        message: 'Временная инструкция досрочно прекращена.',
        color: 'green',
      })
      void loadJobs()
      void loadTemporaryItems()
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось остановить временную инструкцию',
        color: 'red',
      })
    } finally {
      setApplying(false)
    }
  }

  const onLoadProductModels = async () => {
    const pid = projectId.trim()
    if (!pid || productOpenaiKey.trim().length < 10) {
      showNotification({
        title: 'OpenAI',
        message: 'Выберите проект и укажите API-ключ OpenAI.',
        color: 'yellow',
      })
      return
    }
    setProductModelsLoading(true)
    try {
      const res = await api.projectProductDocumentOpenAIModels(pid, {
        openai_api_key: productOpenaiKey.trim(),
      })
      const ids = (res.models || []).map((m) => m.id).filter(Boolean)
      setProductModelOptions(ids)
      if (ids.length && !ids.includes(productModel)) setProductModel(ids[0])
      showNotification({
        title: 'Модели загружены',
        message: ids.length ? `Доступно моделей: ${ids.length}` : 'OpenAI вернул пустой список моделей.',
        color: ids.length ? 'green' : 'yellow',
      })
    } catch (e) {
      showNotification({
        title: 'OpenAI',
        message: e instanceof ApiError ? e.message : 'Не удалось получить список моделей',
        color: 'red',
      })
    } finally {
      setProductModelsLoading(false)
    }
  }

  useEffect(() => {
    if (!applyNow && !applyAtLocal) {
      const dt = new Date(Date.now() + 10 * 60 * 1000)
      const local = new Date(dt.getTime() - dt.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
      setApplyAtLocal(local)
    }
  }, [applyNow, applyAtLocal])

  useEffect(() => {
    if (!tempStartLocal) {
      const start = new Date(Date.now() + 24 * 60 * 60 * 1000)
      const localStart = new Date(start.getTime() - start.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
      setTempStartLocal(localStart)
    }
    if (!tempEndLocal) {
      const end = new Date(Date.now() + 4 * 24 * 60 * 60 * 1000)
      const localEnd = new Date(end.getTime() - end.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
      setTempEndLocal(localEnd)
    }
  }, [tempStartLocal, tempEndLocal])

  const selectedMaster = masterItems.find((m) => m.key === selectedKey)
  const selectedItem = useMemo(() => {
    if (!pkg || !selectedMaster) return null
    const { type, idx } = selectedMaster
    if (type === 'replace_instructions') return pkg.replacements.instructions[idx]
    if (type === 'add_instructions') return pkg.additions.instructions[idx]
    if (type === 'replace_kb') return pkg.replacements.kb[idx]
    if (type === 'add_kb') return pkg.additions.kb[idx]
    return null
  }, [pkg, selectedMaster])

  const toggleInclude = (key: string, on: boolean) => {
    setIncluded((prev) => {
      const n = new Set(prev)
      if (on) n.add(key)
      else n.delete(key)
      return n
    })
  }

  const patchField = (type: string, idx: number, key: string, value: unknown) => {
    setPkg((p) => (p ? updateActItemField(p, type, idx, key, value) : p))
  }

  if (!canAct) {
    return (
      <PageShell>
        <Alert color="gray" variant="light" title="Нет доступа">
          Недостаточно прав для раздела «Актуализация».
        </Alert>
      </PageShell>
    )
  }

  return (
    <PageShell>
      <Stack gap="md">
        <Card withBorder padding="md" radius="md">
          <Grid gutter="md">
            <Grid.Col span={{ base: 12, md: 4 }}>
              <Text size="sm" fw={600} mb="xs">
                Проект
              </Text>
              <NativeSelect
                data={[
                  { value: '', label: 'Выберите проект…' },
                  ...projects.map((p) => ({ value: p.id, label: p.name || p.slug || p.id })),
                ]}
                value={projectId}
                onChange={(e) => setProjectId(e.currentTarget.value)}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 4 }}>
              <Text size="sm" fw={600} mb="xs">
                Режим
              </Text>
              <NativeSelect
                data={[
                  { value: 'diff', label: 'Точечная актуализация' },
                  { value: 'product_document', label: 'Продуктовый документ' },
                  { value: 'temporary_instruction', label: 'Временная инструкция' },
                ]}
                value={mode}
                onChange={(e) => {
                  const nextMode = e.currentTarget.value as ActualizationMode
                  setMode(nextMode)
                  if (nextMode === 'product_document' || nextMode === 'temporary_instruction') setApplyNow(true)
                  setPkg(null)
                  setProductAiPatch(null)
                  setTempPack(null)
                  setSelectedKey(null)
                }}
              />
            </Grid.Col>
            {isProductDocumentMode || isTemporaryMode ? (
              <Grid.Col span={{ base: 12, md: 4 }}>
                <Text size="sm" fw={600} mb="xs">
                  OpenAI
                </Text>
                <Group gap="xs" align="flex-end" wrap="nowrap">
                  <TextInput
                    type="password"
                    placeholder="sk-..."
                    value={productOpenaiKey}
                    onChange={(e) => setProductOpenaiKey(e.currentTarget.value)}
                    style={{ flex: 1 }}
                  />
                  <Button
                    variant="light"
                    onClick={() => void onLoadProductModels()}
                    loading={productModelsLoading}
                  >
                    Модели
                  </Button>
                </Group>
              </Grid.Col>
            ) : null}
            {isProductDocumentMode || isTemporaryMode ? (
              <Grid.Col span={{ base: 12, md: 4 }}>
                <Text size="sm" fw={600} mb="xs">
                  Модель генерации
                </Text>
                {productModelOptions.length ? (
                  <NativeSelect
                    data={productModelOptions.map((id) => ({ value: id, label: id }))}
                    value={productModel}
                    onChange={(e) => setProductModel(e.currentTarget.value)}
                  />
                ) : (
                  <TextInput
                    value={productModel}
                    onChange={(e) => setProductModel(e.currentTarget.value)}
                    placeholder="gpt-5.2"
                  />
                )}
              </Grid.Col>
            ) : null}
            {isProductDocumentMode ? (
              <Grid.Col span={{ base: 12, md: 4 }}>
                <Text size="sm" fw={600} mb="xs">
                  Как применить
                </Text>
                <NativeSelect
                  data={[
                    { value: 'add', label: 'Добавить/обновить выбранное' },
                    { value: 'replace_all', label: 'Полностью заменить активные материалы' },
                    { value: 'no_materials', label: 'Не добавлять материалы, только промпт/переменные' },
                  ]}
                  value={productApplyMode}
                  onChange={(e) => setProductApplyMode(e.currentTarget.value as ProductApplyMode)}
                />
              </Grid.Col>
            ) : null}
            <Grid.Col span={{ base: 12, md: isProductDocumentMode || isTemporaryMode ? 8 : 4 }}>
              <Text size="sm" fw={600} mb="xs">
                {isProductDocumentMode ? 'Продуктовый документ' : isTemporaryMode ? 'Описание временной инструкции' : 'Описание изменений'}
              </Text>
              <Textarea
                placeholder={
                  isProductDocumentMode
                    ? 'Вставьте продуктовый документ: платформы, ограничения, сценарии, ссылки, правила передачи оператору…'
                    : isTemporaryMode
                      ? 'Например: с 3 по 6 июня действует промокод SUMMER, сайт promo.example.com, бот должен объяснять условия и не применять промокод вручную…'
                    : 'Например: обновить тариф 1590 для новых пользователей'
                }
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                minRows={isProductDocumentMode ? 10 : isTemporaryMode ? 6 : 4}
              />
              <Group justify="flex-end" mt="sm">
                <Button
                  leftSection={<IconSparkles size={16} stroke={1.5} />}
                  onClick={() => void onAnalyze()}
                  loading={analyzing}
                >
                  {isProductDocumentMode ? 'Сгенерировать комплект' : isTemporaryMode ? 'Сгенерировать инструкцию' : 'Анализировать'}
                </Button>
              </Group>
            </Grid.Col>
          </Grid>
        </Card>

        {isProductDocumentMode ? (
          <Alert color="blue" variant="light" title="Совместимый режим обновления">
            Этот режим не меняет проект автоматически. Сначала генерируется diff, системный промпт и переменные,
            затем администратор явно выбирает изменения и нажимает «Сохранить».
          </Alert>
        ) : null}

        {isTemporaryMode ? (
          <Alert color="blue" variant="light" title="Временная инструкция">
            Инструкция создаётся неактивной, включается в дату старта, выключается в дату окончания.
            Запланированную можно изменить или удалить; активную — досрочно прекратить.
          </Alert>
        ) : null}

        {analyzing ? <PageCenterLoader label="Анализ AI…" minHeight={120} /> : null}

        {rawAnalyzeError ? (
          <Alert color="red" title="Ошибка анализа">
            {rawAnalyzeError}
          </Alert>
        ) : null}

        {tempPack ? (
          <Card id="temporary-instruction-editor" withBorder padding="md" radius="md">
            <Group justify="space-between" mb="sm" wrap="wrap">
              <Text fw={600} size="sm">
                {editingTempId ? 'Редактирование временной инструкции' : 'Сгенерированная временная инструкция'}
              </Text>
              {editingTempId ? (
                <Button
                  size="compact-xs"
                  variant="subtle"
                  onClick={() => {
                    setEditingTempId(null)
                    setTempPack(null)
                  }}
                >
                  Отменить редактирование
                </Button>
              ) : null}
            </Group>
            {(tempPack.warnings || []).length ? (
              <Alert color="yellow" title="Предупреждения" mb="sm">
                {(tempPack.warnings || []).map((w, i) => (
                  <Text key={i} size="sm">{w}</Text>
                ))}
              </Alert>
            ) : null}
            <SimpleGrid cols={{ base: 1, md: 3 }} spacing="sm" mb="sm">
              <TextInput
                label="Название"
                value={tempTitle}
                onChange={(e) => setTempTitle(e.currentTarget.value)}
              />
              <TextInput
                label="Начало действия (локальное время)"
                type="datetime-local"
                value={tempStartLocal}
                onChange={(e) => setTempStartLocal(e.currentTarget.value)}
              />
              <TextInput
                label="Окончание действия (локальное время)"
                type="datetime-local"
                value={tempEndLocal}
                onChange={(e) => setTempEndLocal(e.currentTarget.value)}
              />
            </SimpleGrid>
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="sm">
              <Textarea
                label="Триггеры"
                value={String(tempPack.instruction?.triggers || '')}
                onChange={(e) =>
                  setTempPack((p) => p ? { ...p, instruction: { ...(p.instruction || {}), triggers: e.target.value } } : p)
                }
                autosize
                minRows={3}
              />
              <Textarea
                label="Ответ/рекомендация"
                value={String(tempPack.instruction?.response_from_db || '')}
                onChange={(e) =>
                  setTempPack((p) => p ? { ...p, instruction: { ...(p.instruction || {}), response_from_db: e.target.value } } : p)
                }
                autosize
                minRows={3}
              />
              <Textarea
                label="Действие"
                value={String(tempPack.instruction?.action || '')}
                onChange={(e) =>
                  setTempPack((p) => p ? { ...p, instruction: { ...(p.instruction || {}), action: e.target.value } } : p)
                }
                autosize
                minRows={3}
              />
              <Textarea
                label="Когда переводить оператору"
                value={String(tempPack.instruction?.when_transfer_to_operator || '')}
                onChange={(e) =>
                  setTempPack((p) => p ? { ...p, instruction: { ...(p.instruction || {}), when_transfer_to_operator: e.target.value } } : p)
                }
                autosize
                minRows={3}
              />
            </SimpleGrid>
            <Group justify="flex-end" mt="sm">
              <Button color="green" onClick={() => void onScheduleTemporaryInstruction()} loading={applying}>
                {editingTempId ? 'Сохранить изменения' : 'Запланировать'}
              </Button>
            </Group>
          </Card>
        ) : null}

        {pkg ? (
          <>
            <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
              <Card withBorder padding="sm" radius="md">
                <Text size="xs" c="dimmed">
                  Замена инструкций
                </Text>
                <Text fw={700} size="xl">
                  {pkg.replacements.instructions.length}
                </Text>
              </Card>
              <Card withBorder padding="sm" radius="md">
                <Text size="xs" c="dimmed">
                  Новые инструкции
                </Text>
                <Text fw={700} size="xl">
                  {pkg.additions.instructions.length}
                </Text>
              </Card>
              <Card withBorder padding="sm" radius="md">
                <Text size="xs" c="dimmed">
                  Замена KB
                </Text>
                <Text fw={700} size="xl">
                  {pkg.replacements.kb.length}
                </Text>
              </Card>
              <Card withBorder padding="sm" radius="md">
                <Text size="xs" c="dimmed">
                  Новое в KB
                </Text>
                <Text fw={700} size="xl">
                  {pkg.additions.kb.length}
                </Text>
              </Card>
            </SimpleGrid>

            {(pkg.warnings || []).length > 0 ? (
              <Alert color="yellow" title="Предупреждения">
                {(pkg.warnings || []).map((w, i) => (
                  <Text key={i} size="sm">
                    {w}
                  </Text>
                ))}
              </Alert>
            ) : null}

            {pkg.quality_report && (pkg.quality_report.items || []).length > 0 ? (
              <QualityReportSection report={pkg.quality_report} />
            ) : null}

            {productAiPatch ? (
              <Card withBorder padding="md" radius="md">
                <Group justify="space-between" mb="sm" wrap="wrap">
                  <Text fw={600} size="sm">
                    Настройки проекта из продуктового документа
                  </Text>
                  <Badge variant="light">
                    переменных: {(productAiPatch.variables || []).length}
                  </Badge>
                </Group>
                <Textarea
                  label="Системный промпт проекта"
                  value={productAiPatch.instructions_system_prompt || ''}
                  readOnly
                  autosize
                  minRows={4}
                  maxRows={12}
                />
                {(productAiPatch.variables || []).length ? (
                  <Stack gap="xs" mt="sm">
                    {(productAiPatch.variables || []).map((v, i) => (
                      <Card key={`${v.key || 'var'}-${i}`} withBorder padding="xs" radius="md">
                        <Group justify="space-between" gap="xs" wrap="wrap">
                          <Text fw={600} size="sm">
                            {v.key || 'variable'}
                          </Text>
                          <Badge size="xs" variant="light" color={v.required ? 'blue' : 'gray'}>
                            priority {v.priority ?? '—'}
                          </Badge>
                        </Group>
                        <Text size="sm" c="dimmed">
                          {v.ask_label || v.description || 'Без описания'}
                        </Text>
                      </Card>
                    ))}
                  </Stack>
                ) : null}
              </Card>
            ) : null}

            {masterItems.length === 0 ? (
              <EmptyState
                title={productAiPatch ? 'Нет изменений в инструкциях и KB' : 'Нет изменений'}
                description={
                  productAiPatch
                    ? 'Можно сохранить только системный промпт проекта и переменные сбора данных.'
                    : 'Модель не предложила правок по запросу.'
                }
              />
            ) : (
              <Grid gutter="md">
                <Grid.Col span={{ base: 12, md: 3 }}>
                  <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb="xs">
                    Список изменений
                  </Text>
                  <ScrollArea h={480} type="auto">
                    <Stack gap={4}>
                      {masterItems.map((it) => (
                        <Button
                          key={it.key}
                          variant={selectedKey === it.key ? 'light' : 'subtle'}
                          color={selectedKey === it.key ? 'blue' : 'gray'}
                          fullWidth
                          styles={{ inner: { justifyContent: 'flex-start' } }}
                          onClick={() => setSelectedKey(it.key)}
                        >
                          <Stack gap={2} align="flex-start" style={{ textAlign: 'left' }}>
                            <Text size="xs" c="dimmed" lineClamp={1}>
                              {it.label}
                            </Text>
                            <Text size="sm" lineClamp={2}>
                              {it.preview}
                            </Text>
                          </Stack>
                        </Button>
                      ))}
                    </Stack>
                  </ScrollArea>
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 9 }}>
                  {selectedMaster && selectedItem ? (
                    <ActDiffCard
                      master={selectedMaster}
                      item={selectedItem}
                      included={included.has(selectedMaster.key)}
                      onIncluded={(on) => toggleInclude(selectedMaster.key, on)}
                      onPatch={(key, val) =>
                        patchField(selectedMaster.type, selectedMaster.idx, key, val)
                      }
                    />
                  ) : null}
                </Grid.Col>
              </Grid>
            )}

            <Card withBorder padding="md" radius="md">
              <Group justify="space-between" align="flex-end" wrap="wrap" gap="md">
                <Stack gap="sm">
                  <Checkbox
                    label={isProductDocumentMode ? 'Применить комплект немедленно' : 'Применить немедленно'}
                    checked={applyNow}
                    disabled={isProductDocumentMode}
                    onChange={(e) => setApplyNow(e.currentTarget.checked)}
                  />
                  {isProductDocumentMode ? (
                    <Text size="xs" c="dimmed">
                      Отложенное применение для продуктового комплекта пока отключено: так мы не теряем связку diff,
                      системного промпта и переменных.
                    </Text>
                  ) : (
                    <TextInput
                      label="Дата и время применения (локальное)"
                      type="datetime-local"
                      disabled={applyNow}
                      value={applyAtLocal}
                      onChange={(e) => setApplyAtLocal(e.currentTarget.value)}
                    />
                  )}
                </Stack>
                <Button
                  leftSection={<IconCheck size={16} stroke={1.5} />}
                  color="green"
                  onClick={() => void onApply()}
                  loading={applying}
                  disabled={!masterItems.length && !productAiPatch}
                >
                  Сохранить
                </Button>
              </Group>
            </Card>
          </>
        ) : null}

        {projectId ? (
          <Card withBorder padding="sm" radius="md">
            <Group justify="space-between" mb="xs">
              <Text fw={600} size="sm">
                Запланированные применения
              </Text>
              <Button
                size="compact-xs"
                variant="light"
                leftSection={<IconRefresh size={14} stroke={1.5} />}
                onClick={() => void loadJobs()}
              >
                Обновить
              </Button>
            </Group>
            {jobs.length === 0 ? (
              <Text size="sm" c="dimmed">
                Нет задач.
              </Text>
            ) : (
              <Stack gap="xs">
                {jobs.map((j) => (
                  <Card key={j.id} withBorder padding="xs" radius="md">
                    <Group justify="space-between" wrap="wrap" gap="xs">
                      <Stack gap={2}>
                        <Text size="sm" fw={600}>
                          {String(j.payload?._action || 'actualization')}
                        </Text>
                        {j.payload?.title ? (
                          <Text size="xs" c="dimmed">{String(j.payload.title)}</Text>
                        ) : null}
                      </Stack>
                      <Badge variant="light" color={j.status === 'done' ? 'green' : j.status === 'failed' ? 'red' : 'blue'}>
                        {j.status}
                      </Badge>
                    </Group>
                    <Text size="xs" c="dimmed" mt={4}>
                      Выполнить: {j.apply_at_utc ? formatDateTimeDot(j.apply_at_utc) : '—'} · создано: {formatDateTimeDot(j.created_at)}
                    </Text>
                    {j.error ? <Text size="xs" c="red">{j.error}</Text> : null}
                  </Card>
                ))}
              </Stack>
            )}
          </Card>
        ) : null}

        {projectId && isTemporaryMode ? (
          <Card withBorder padding="sm" radius="md">
            <Group justify="space-between" mb="xs">
              <Text fw={600} size="sm">
                История временных инструкций
              </Text>
              <Button size="compact-xs" variant="light" onClick={() => void loadTemporaryItems()}>
                Обновить
              </Button>
            </Group>
            {tempItems.length === 0 ? (
              <Text size="sm" c="dimmed">Пока нет временных инструкций.</Text>
            ) : (
              <Stack gap="xs">
                {tempItems.map((it, i) => {
                  const status = String(it.status || (it.is_active ? 'active' : 'ended'))
                  const statusLabel =
                    status === 'scheduled'
                      ? 'запланирована'
                      : status === 'active'
                        ? 'активна'
                        : status === 'stopped'
                          ? 'остановлена'
                          : 'завершена'
                  const statusColor =
                    status === 'scheduled'
                      ? 'blue'
                      : status === 'active'
                        ? 'green'
                        : status === 'stopped'
                          ? 'orange'
                          : 'gray'
                  const canStop = status === 'scheduled' || status === 'active'
                  const canEdit = true
                  return (
                  <Card key={String(it.id || i)} withBorder padding="xs" radius="md">
                    <Group justify="space-between" wrap="wrap">
                      <Text fw={600} size="sm">{String(it.title || 'Временная инструкция')}</Text>
                      <Badge color={statusColor} variant="light">
                        {statusLabel}
                      </Badge>
                    </Group>
                    <Text size="xs" c="dimmed">
                      {it.start_at_utc ? formatDateTimeDot(String(it.start_at_utc)) : '—'} · до{' '}
                      {it.end_at_utc ? formatDateTimeDot(String(it.end_at_utc)) : '—'}
                    </Text>
                    <Group justify="flex-end" mt="xs" gap={6}>
                      {canEdit ? (
                        <Button size="compact-xs" variant="light" onClick={() => editTemporaryItem(it)}>
                          Изменить
                        </Button>
                      ) : null}
                      {canStop ? (
                        <Button
                          size="compact-xs"
                          variant="light"
                          color="orange"
                          loading={applying}
                          onClick={() => void onStopTemporaryItem(it)}
                        >
                          {status === 'scheduled' ? 'Отменить включение' : 'Досрочно прекратить'}
                        </Button>
                      ) : null}
                      <Button
                        size="compact-xs"
                        variant="light"
                        color="red"
                        loading={applying}
                        onClick={() => void onDeleteTemporaryItem(it)}
                      >
                        Удалить
                      </Button>
                    </Group>
                  </Card>
                  )
                })}
              </Stack>
            )}
          </Card>
        ) : null}
      </Stack>
    </PageShell>
  )
}

function ActDiffCard({
  master,
  item,
  included,
  onIncluded,
  onPatch,
}: {
  master: ActMasterItem
  item: ActItem
  included: boolean
  onIncluded: (v: boolean) => void
  onPatch: (key: string, value: unknown) => void
}) {
  const fields = effectiveFieldsForRow(master.type, item)
  const existing = item.existing_snapshot || {}
  return (
    <Card withBorder padding="md" radius="md">
      <Group justify="space-between" mb="md" wrap="wrap">
        <Badge variant="light">{ACT_TYPE_LABELS[master.type] || master.type}</Badge>
        <Checkbox label="Включить в применение" checked={included} onChange={(e) => onIncluded(e.currentTarget.checked)} />
      </Group>
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        <Stack gap="sm">
          <Text size="xs" fw={700} tt="uppercase" c="dimmed">
            Было
          </Text>
          {fields.map((k) => (
            <ActReadonlyField key={k} fieldKey={k} value={(existing as Record<string, unknown>)[k]} />
          ))}
        </Stack>
        <Stack gap="sm">
          <Text size="xs" fw={700} tt="uppercase" c="dimmed">
            Станет (редактируемо)
          </Text>
          {fields.map((k) => (
            <ActEditField
              key={k}
              fieldKey={k}
              value={getEditableValue(master.type, item, k)}
              onChange={(v) => onPatch(k, v)}
            />
          ))}
        </Stack>
      </SimpleGrid>
    </Card>
  )
}
